import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { BRAND_MINT, COMPLIANCE_BADGE, badgePill } from '@/lib/utils'
import {
  getSignatoriesApi,
  createSignatoryApi,
  updateSignatoryApi,
  getLegalDocumentsApi,
  type Signatory,
} from '@/lib/api'
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ClockIcon,
  FileSignatureIcon,
  GlobeIcon,
  HashIcon,
  AlertTriangleIcon,
  PenLineIcon,
  ShieldCheckIcon,
  UserIcon,
} from 'lucide-react'

/** Live loose-match hint (mirrors the API's non-blocking G4 check). */
function typedNameStatus(typed: string, legal: string): 'exact' | 'weak' | 'none' {
  const norm = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean)
  const t = norm(typed)
  const l = norm(legal)
  if (t.length === 0 || l.length === 0) return 'none'
  if (t.join(' ') === l.join(' ')) return 'exact'
  const covered = t.every(
    (tok) => l.includes(tok) || (tok.length === 1 && l.some((lt) => lt.startsWith(tok))),
  )
  return covered ? 'weak' : 'none'
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function truncateHash(h: string | null): string {
  if (!h) return '—'
  return `${h.slice(0, 12)}…`
}

export const Route = createFileRoute('/dashboard/company-profile/signatory')({
  component: () => {
    const navigate = useNavigate()

    const [signatories, setSignatories] = useState<Signatory[]>([])
    const [terms, setTerms] = useState<{ version: string; content: string } | null>(null)
    const [dpa, setDpa] = useState<{ version: string; content: string } | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Signing form state
    const [fullLegalName, setFullLegalName] = useState('')
    const [jobTitle, setJobTitle] = useState('')
    const [corporateEmail, setCorporateEmail] = useState('')
    const [agreed, setAgreed] = useState(false)
    const [typedName, setTypedName] = useState('')
    const [saving, setSaving] = useState(false)
    const [justSigned, setJustSigned] = useState<Signatory | null>(null)

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const [signatoriesRes, legalRes] = await Promise.all([
          getSignatoriesApi(),
          getLegalDocumentsApi(),
        ])
        setSignatories(signatoriesRes.data)
        setTerms(legalRes.terms)
        setDpa(legalRes.dpa)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load signatory details')
        toast.error(err instanceof Error ? err.message : 'Failed to load signatory details')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      load()
      return () => { /* cancelled by state guard */ }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const activeRep = useMemo(
      () => signatories.find((s) => s.is_active) ?? null,
      [signatories],
    )
    const activeSigned = activeRep && activeRep.typed_name ? activeRep : null
    const showSignForm = !activeSigned

    const nameStatus = typedNameStatus(typedName, fullLegalName)
    const formComplete =
      fullLegalName.trim().length > 0 &&
      jobTitle.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(corporateEmail.trim()) &&
      agreed &&
      typedName.trim().length > 0

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!formComplete) return

      setSaving(true)
      try {
        const res = await createSignatoryApi({
          full_legal_name: fullLegalName.trim(),
          job_title: jobTitle.trim(),
          corporate_email: corporateEmail.trim(),
          authority_confirmed: true,
          typed_name: typedName.trim(),
          terms_version: terms?.version,
          dpa_version: dpa?.version,
        })
        if (res.typed_name_mismatch) {
          toast.warning(
            'Signed, but the typed signature does not closely match the legal name — please double-check.',
          )
        } else {
          toast.success('Document e-signed')
        }
        setJustSigned(res.signatory)
        setFullLegalName('')
        setJobTitle('')
        setCorporateEmail('')
        setAgreed(false)
        setTypedName('')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to capture signature')
      } finally {
        setSaving(false)
      }
    }

    const handleSignNew = async () => {
      if (!activeRep) return
      setSaving(true)
      try {
        await updateSignatoryApi(activeRep.rep_id, { is_active: false })
        setJustSigned(null)
        toast.success('Previous representative deactivated — you can sign a new one.')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to deactivate representative')
      } finally {
        setSaving(false)
      }
    }

    if (loading) {
      return <SignatorySkeleton />
    }

    if (error) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate({ to: '/dashboard/company-profile' })}>
              Go back
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-8">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-4 -ml-2 gap-1.5"
            onClick={() => navigate({ to: '/dashboard/company-profile' })}
          >
            <ArrowLeftIcon className="size-4" />
            Back to company profile
          </Button>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Legal signatory</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Appoint the person authorised to bind your company to the Terms of Service and Data Processing Agreement, and capture their in-app electronic signature.
              </p>
            </div>
          </div>
        </div>

        {/* ── Signing flow / signed confirmation (U33a–U33d) ────────────── */}
        {activeSigned || justSigned ? (
          <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-800/40 dark:bg-emerald-950/20">
            <div className="flex items-start gap-3">
              <CheckCircle2Icon className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                  Document e-signed by {justSigned?.full_legal_name ?? activeSigned?.full_legal_name}
                </p>
                <p className="mt-0.5 text-sm text-emerald-700 dark:text-emerald-400">
                  Typed signature: <strong>{justSigned?.typed_name ?? activeSigned?.typed_name}</strong>
                  {' · '}
                  Signed {formatDate(justSigned?.signed_at ?? activeSigned?.signed_at ?? null)}
                  {' · '}
                  {justSigned?.terms_version ?? activeSigned?.terms_version} ToS / {justSigned?.dpa_version ?? activeSigned?.dpa_version} DPA
                </p>
                <p className="mt-1 break-all font-mono text-xs text-emerald-700/80 dark:text-emerald-400/80">
                  SHA-256: {truncateHash(justSigned?.document_version_hash ?? activeSigned?.document_version_hash ?? null)}
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={handleSignNew} disabled={saving}>
                <PenLineIcon className="size-4" />
                Sign a new representative
              </Button>
            </div>
          </div>
        ) : null}

        {/* ── Signing form (U33a–U33d) ─────────────────────────────────── */}
        {showSignForm && (
          <form className="space-y-8" onSubmit={handleSubmit}>
            <section className="space-y-6 rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-2">
                <UserIcon className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Signatory details
                </h2>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullLegalName">Full legal name</Label>
                <Input
                  id="fullLegalName"
                  type="text"
                  placeholder="Jane Doe"
                  value={fullLegalName}
                  onChange={(e) => setFullLegalName(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job title</Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder="Chief Financial Officer, Director, Head of Procurement…"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Must indicate authority to bind the company.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="corporateEmail">Corporate email</Label>
                <Input
                  id="corporateEmail"
                  type="email"
                  placeholder="jane@acme.com"
                  value={corporateEmail}
                  onChange={(e) => setCorporateEmail(e.target.value)}
                  disabled={saving}
                />
              </div>
            </section>

            {/* U33a — ToS + DPA disclosure (same canonical copy the API hashes) */}
            <section className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-2">
                <ShieldCheckIcon className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Agreements — please read before signing
                </h2>
              </div>

              {terms && (
                <details className="group mb-3 rounded-lg border border-border bg-muted/30">
                  <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium select-none">
                    <span>Terms of Service — {terms.version}</span>
                    <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap border-t border-border px-4 py-3 font-mono text-xs leading-relaxed text-muted-foreground">
                    {terms.content}
                  </div>
                </details>
              )}

              {dpa && (
                <details className="group rounded-lg border border-border bg-muted/30">
                  <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium select-none">
                    <span>Data Processing Agreement — {dpa.version}</span>
                    <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap border-t border-border px-4 py-3 font-mono text-xs leading-relaxed text-muted-foreground">
                    {dpa.content}
                  </div>
                </details>
              )}
            </section>

            {/* U33b — mandatory checkbox */}
            <section className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agreed"
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(v === true)}
                  disabled={saving}
                  className="mt-0.5"
                />
                <Label htmlFor="agreed" className="text-sm leading-relaxed">
                  I confirm I have read and agree to the{' '}
                  <strong>Terms of Service</strong> and the{' '}
                  <strong>Data Processing Agreement</strong>, and I have the authority
                  to bind my company to them.
                </Label>
              </div>
            </section>

            {/* U33c — typed signature */}
            <section className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-2">
                <FileSignatureIcon className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Electronic signature
                </h2>
              </div>
              <div className="mt-4 space-y-2">
                <Label htmlFor="typedName">Type your full legal name to act as your electronic signature</Label>
                <Input
                  id="typedName"
                  type="text"
                  placeholder="Jane Doe"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  disabled={saving}
                  className="font-serif text-lg"
                />
                {fullLegalName.trim() && typedName.trim() && nameStatus === 'exact' && (
                  <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2Icon className="size-3.5" /> Matches the legal name.
                  </p>
                )}
                {fullLegalName.trim() && typedName.trim() && nameStatus === 'weak' && (
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangleIcon className="size-3.5" /> Doesn't closely match the legal name — you can still sign, but please verify it's intentional.
                  </p>
                )}
                {fullLegalName.trim() && typedName.trim() && nameStatus === 'none' && (
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangleIcon className="size-3.5" /> This signature looks different from the legal name entered above.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Your typed name, browser fingerprint (IP + user agent), and a SHA-256 hash of the exact agreement text are recorded server-side for audit.
                </p>
              </div>
            </section>

            <div className="flex items-center gap-4">
              <Button type="submit" disabled={saving || !formComplete} className="gap-1.5">
                {saving ? (
                  <>
                    <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Signing…
                  </>
                ) : (
                  <>
                    <FileSignatureIcon className="size-4" />
                    Sign electronically
                  </>
                )}
              </Button>
              {!agreed && (
                <p className="text-xs text-muted-foreground">
                  Accept the agreements above to enable signing.
                </p>
              )}
            </div>
          </form>
        )}

        {/* ── U34a — signatory list (active + historical) ─────────────── */}
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Signatory history
          </h2>
          {signatories.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No signatories yet. Appoint and sign above to create the first audit record.
            </div>
          ) : (
            <div className="space-y-3">
              {signatories.map((s) => (
                <div key={s.rep_id} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{s.full_legal_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.job_title} · {s.corporate_email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          s.is_active
                            ? badgePill('bg-emerald-500/10 text-emerald-600 dark:text-emerald-400')
                            : badgePill('bg-muted text-muted-foreground')
                        }
                      >
                        {s.is_active ? <ShieldCheckIcon className="size-3" /> : <ClockIcon className="size-3" />}
                        {s.is_active ? 'Active' : 'Historical'}
                      </span>
                      {s.signature_provider === 'native_in_app' && s.typed_name && (
                        <span className={badgePill(COMPLIANCE_BADGE)}>
                          <FileSignatureIcon className="size-3" />
                          In-app signature
                        </span>
                      )}
                    </div>
                  </div>
                  {s.typed_name ? (
                    <dl className="mt-3 grid gap-2 border-t border-border pt-3 text-xs sm:grid-cols-2">
                      <div className="flex items-center gap-2">
                        <PenLineIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        <dt className="text-muted-foreground">Signed as</dt>
                        <dd className="font-medium">{s.typed_name}</dd>
                      </div>
                      <div className="flex items-center gap-2">
                        <GlobeIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        <dt className="text-muted-foreground">Signed</dt>
                        <dd className="font-medium">{formatDate(s.signed_at)}</dd>
                      </div>
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <HashIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        <dt className="text-muted-foreground">Doc hash</dt>
                        <dd className="break-all font-mono text-[11px]">{truncateHash(s.document_version_hash)}</dd>
                      </div>
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <ShieldCheckIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        <dt className="text-muted-foreground">Versions</dt>
                        <dd className="font-medium">{s.terms_version ?? '—'} ToS / {s.dpa_version ?? '—'} DPA</dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
                      Not yet e-signed.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    )
  },
})

function SignatorySkeleton() {
  return (
    <div className="mx-auto max-w-2xl animate-pulse">
      <div className="mb-8">
        <div className="h-7 w-48 rounded bg-muted" />
      </div>
      <div className="space-y-6 rounded-xl border border-border p-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-10 w-full rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  )
}
